import { useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  Building2,
  CheckCircle2,
  FileText,
  Mail,
  Pencil,
  Phone,
  User,
  X,
  MapPin,
  Activity,
  Ban,
  Download,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

type Status = "Pending" | "In Review" | "Approved" | "Rejected";

interface SpaceItem {
  name: string;
  location: string;
  type: string;
  image: string;
  status?: "Pending" | "Active" | "Rejected";
}

interface DocItem {
  label: string;
  file: string;
}

interface ApplicationDetail {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  status: Status;
  statusLabel: string;
  plan: string;
  tag: string;
  submitted: string;
  submittedRelative: string;
  businessName: string;
  businessType: string;
  tradeLicense: string;
  location: string;
  website: string;
  joinedDate: string;
  spacesCount: number;
  totalBookings: number;
  avgRating: string;
  subscription: string;
  accountStatus: string;
  kycStatus: string;
  bank: string;
  iban: string;
  payoutCycle: string;
  spaces: SpaceItem[];
  documents: DocItem[];
}

const applications: Record<string, ApplicationDetail> = {
  "1": {
    id: "1",
    name: "Khalid Al-Rashidi",
    email: "khalid.alrashidi@gmail.com",
    phone: "+971 50 234 8812",
    avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=200&h=200&fit=crop&crop=faces",
    status: "Pending",
    statusLabel: "Pending Approval",
    plan: "Premium Plan",
    tag: "F&B · Dubai",
    submitted: "Jun 10, 2025",
    submittedRelative: "2 days ago",
    businessName: "Al-Rashidi Hospitality LLC",
    businessType: "Food & Beverage / Lounge",
    tradeLicense: "DED-2024-11-887643",
    location: "Dubai Marina, Dubai, UAE",
    website: "alrashidi-hospitality.com",
    joinedDate: "June 10, 2025",
    spacesCount: 3,
    totalBookings: 0,
    avgRating: "—",
    subscription: "Premium · SAR 299/mo",
    accountStatus: "Under Review",
    kycStatus: "Verified",
    bank: "Emirates NBD",
    iban: "AE07 0331 2345 6789 0123 456",
    payoutCycle: "Bi-weekly",
    spaces: [
      {
        name: "The Amber Lounge",
        location: "Dubai Marina",
        type: "Café & Lounge",
        image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=200&h=200&fit=crop",
      },
      {
        name: "Marina Sky Terrace",
        location: "JBR, Dubai",
        type: "Rooftop Bar",
        image: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=200&h=200&fit=crop",
      },
      {
        name: "The Private Dining Room",
        location: "Downtown Dubai",
        type: "Private Dining",
        image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200&h=200&fit=crop",
      },
    ],
    documents: [
      { label: "Trade License", file: "DED-2024-11-887643.pdf" },
      { label: "Emirates ID", file: "emiratesid_khalid.pdf" },
      { label: "Bank Statement", file: "bank_statement_may25.pdf" },
    ],
  },
};

const fallback = (id: string): ApplicationDetail => ({
  ...applications["1"],
  id,
});

export const Route = createFileRoute("/admin/approvals/$id")({
  head: ({ params }) => ({
    meta: [{ title: `Review application #${params.id} — Spayzo Admin` }, { name: "robots", content: "noindex" }],
  }),
  loader: ({ params }): ApplicationDetail => {
    if (!params.id) throw notFound();
    return applications[params.id] ?? fallback(params.id);
  },
  component: Page,
});

const BRAND = "#5958F3";

function Page() {
  const { t } = useTranslation();
  const app = Route.useLoaderData() as ApplicationDetail;
  const [previewDoc, setPreviewDoc] = useState<DocItem | null>(null);
  const [notes, setNotes] = useState<{ id: string; text: string; author: string; at: string }[]>([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const submitNote = () => {
    const text = noteDraft.trim();
    if (!text) return;
    setNotes((prev) => [
      {
        id: crypto.randomUUID(),
        text,
        author: "Alex Morgan",
        at: new Date().toLocaleString(),
      },
      ...prev,
    ]);
    setNoteDraft("");
    setAddingNote(false);
    toast.success(t("adminApprovalDetail.noteAdded"));
  };

  const deleteNote = (id: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
    toast.success(t("adminApprovalDetail.noteRemoved"));
  };

  return (
    <AdminShell title={t("pages.admin.approvalDetail.title")} subtitle={t("adminApprovalDetail.application", { id: app.id })}>
      <Card className="overflow-hidden rounded-3xl border-border/60 bg-background p-0 shadow-soft">
        {/* Header */}
        <div className="flex flex-wrap items-start gap-5 p-6 sm:p-8">
          <img src={app.avatar} alt={app.name} className="h-20 w-20 shrink-0 rounded-2xl object-cover" />
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-2xl font-bold tracking-tight">{app.name}</h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {app.email}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" /> {app.phone}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                {app.statusLabel}
              </span>
              <span
                className="rounded-full px-3 py-1 text-xs font-semibold"
                style={{ backgroundColor: "#EEEEFE", color: BRAND }}
              >
                {app.plan}
              </span>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-foreground">{app.tag}</span>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-end">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t("adminApprovalDetail.submitted")}</p>
              <p className="font-display text-base font-bold tracking-tight">{app.submitted}</p>
              <p className="text-xs text-muted-foreground">{app.submittedRelative}</p>
            </div>
            {/* <Button asChild size="icon" variant="outline" className="h-10 w-10 rounded-xl">
              <Link to="/admin/approvals" aria-label="Close">
                <X className="h-4 w-4" />
              </Link>
            </Button> */}
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <div className="border-b border-border/60 px-6 sm:px-8">
            <TabsList className="h-auto gap-6 rounded-none bg-transparent p-0">
              <TabTrigger value="overview" icon={User} label={t("adminApprovalDetail.tabs.overview")} />
              <TabTrigger value="spaces" icon={Building2} label={t("adminApprovalDetail.tabs.spaces", { count: app.spacesCount })} />
              <TabTrigger value="documents" icon={FileText} label={t("adminApprovalDetail.tabs.documents")} />
              <TabTrigger value="activity" icon={Activity} label={t("adminApprovalDetail.tabs.activity")} />
            </TabsList>
          </div>

          <TabsContent value="overview" className="m-0 space-y-6 p-6 sm:p-8">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Business info */}
              <Card className="rounded-2xl border-0 bg-surface p-6 shadow-none">
                <SectionLabel>{t("adminApprovalDetail.businessInfo")}</SectionLabel>
                <dl className="mt-4 divide-y divide-border/60">
                  <Field label={t("adminApprovalDetail.field.businessName")} value={app.businessName} />
                  <Field label={t("adminApprovalDetail.field.businessType")} value={app.businessType} />
                  <Field label={t("adminApprovalDetail.field.tradeLicense")} value={app.tradeLicense} />
                  <Field label={t("adminApprovalDetail.field.location")} value={app.location} />
                  <Field label={t("adminApprovalDetail.field.joinedDate")} value={app.joinedDate} />
                </dl>
              </Card>

              {/* Performance + Payout */}
              <div className="space-y-6">
                <Card className="rounded-2xl border-0 bg-surface p-6 shadow-none">
                  <SectionLabel>{t("adminApprovalDetail.performance")}</SectionLabel>
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <StatBox value={String(app.spacesCount)} label={t("adminApprovalDetail.stat.spaces")} />
                    <StatBox value={String(app.totalBookings)} label={t("adminApprovalDetail.stat.bookings")} />
                    <StatBox value={app.avgRating} label={t("adminApprovalDetail.stat.rating")} />
                  </div>
                  <dl className="mt-5 space-y-3 text-sm">
                    <RowKV label={t("adminApprovalDetail.subscription")}>
                      <span className="font-semibold" style={{ color: BRAND }}>
                        {app.subscription}
                      </span>
                    </RowKV>
                    <RowKV label={t("adminApprovalDetail.accountStatus")}>
                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                        {app.accountStatus}
                      </span>
                    </RowKV>
                    <RowKV label={t("adminApprovalDetail.kycStatus")}>
                      <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                        {app.kycStatus}
                      </span>
                    </RowKV>
                  </dl>
                </Card>

                <Card className="rounded-2xl border-0 bg-surface p-6 shadow-none">
                  <SectionLabel>{t("adminApprovalDetail.payoutDetails")}</SectionLabel>
                  <dl className="mt-4 divide-y divide-border/60">
                    <Field label={t("adminApprovalDetail.field.bank")} value={app.bank} />
                    <Field label={t("adminApprovalDetail.field.iban")} value={app.iban} />
                    <Field label={t("adminApprovalDetail.field.payoutCycle")} value={app.payoutCycle} />
                  </dl>
                </Card>
              </div>
            </div>

            {/* Submitted Spaces */}
            <Card className="rounded-2xl border-0 bg-surface p-6 shadow-none">
              <div className="flex items-center justify-between">
                <SectionLabel>{t("adminApprovalDetail.submittedSpaces")}</SectionLabel>
              </div>
              <div className="mt-4 space-y-3">
                {app.spaces.map((s) => (
                  <div key={s.name} className="flex items-center gap-4 rounded-2xl bg-background p-3">
                    <img src={s.image} alt={s.name} className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{s.name}</p>
                      <p className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {s.location} · {s.type}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Documents */}
            <Card className="rounded-2xl border-0 bg-surface p-6 shadow-none">
              <SectionLabel>{t("adminApprovalDetail.documents")}</SectionLabel>
              <div className="mt-4 space-y-3">
                {app.documents.map((d) => (
                  <div key={d.file} className="flex items-center gap-4 rounded-2xl bg-background p-4">
                    <span
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                      style={{ backgroundColor: "#EEEEFE", color: BRAND }}
                    >
                      <FileText className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{d.label}</p>
                      <p className="text-xs text-muted-foreground">{d.file}</p>
                    </div>
                    <button
                      onClick={() => setPreviewDoc(d)}
                      className="rounded-lg px-3 py-1.5 text-sm font-semibold hover:underline"
                      style={{ color: BRAND }}
                    >
                      {t("adminApprovalDetail.see")}
                    </button>
                  </div>
                ))}
              </div>
            </Card>

            {/* Admin Notes */}
            <Card className="rounded-2xl border-0 bg-surface p-6 shadow-none">
              <SectionLabel>{t("adminApprovalDetail.adminNotes")}</SectionLabel>

              {notes.length === 0 && !addingNote && (
                <div className="mt-4 rounded-2xl bg-background p-5 text-sm italic text-muted-foreground">
                  {t("adminApprovalDetail.noNotes")}
                </div>
              )}

              {notes.length > 0 && (
                <ul className="mt-4 space-y-3">
                  {notes.map((n) => (
                    <li key={n.id} className="rounded-2xl bg-background p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-foreground">
                            {n.author} <span className="font-normal text-muted-foreground">· {n.at}</span>
                          </p>
                          <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{n.text}</p>
                        </div>
                        <button
                          onClick={() => deleteNote(n.id)}
                          className="text-muted-foreground hover:text-rose-600"
                          aria-label={t("adminApprovalDetail.deleteNote")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {addingNote ? (
                <div className="mt-4 space-y-3">
                  <Textarea
                    autoFocus
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder={t("adminApprovalDetail.notePlaceholder")}
                    className="min-h-24 rounded-2xl bg-background"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAddingNote(false);
                        setNoteDraft("");
                      }}
                    >
                      {t("adminApprovalDetail.cancel")}
                    </Button>
                    <Button
                      size="sm"
                      onClick={submitNote}
                      disabled={!noteDraft.trim()}
                      style={{ backgroundColor: BRAND }}
                      className="text-white hover:opacity-90"
                    >
                      {t("adminApprovalDetail.saveNote")}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setAddingNote(true)}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                  style={{ color: BRAND }}
                >
                  <Pencil className="h-3.5 w-3.5" /> {t("adminApprovalDetail.addNote")}
                </button>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="spaces" className="m-0 p-6 sm:p-8">
            <div className="space-y-3">
              {app.spaces.map((s) => (
                <div key={s.name} className="flex items-center gap-4 rounded-2xl bg-surface p-4">
                  <img src={s.image} alt={s.name} className="h-16 w-16 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{s.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {s.location} · {s.type}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="documents" className="m-0 p-6 sm:p-8">
            <div className="space-y-3">
              {app.documents.map((d) => (
                <div key={d.file} className="flex items-center gap-4 rounded-2xl bg-surface p-4">
                  <span
                    className="grid h-10 w-10 place-items-center rounded-xl"
                    style={{ backgroundColor: "#EEEEFE", color: BRAND }}
                  >
                    <FileText className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{d.label}</p>
                    <p className="text-xs text-muted-foreground">{d.file}</p>
                  </div>
                  <button
                    onClick={() => setPreviewDoc(d)}
                    className="text-sm font-semibold hover:underline"
                    style={{ color: BRAND }}
                  >
                    {t("adminApprovalDetail.see")}
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="activity" className="m-0 p-6 sm:p-8">
            <ol className="space-y-4">
              {[
                { title: t("adminApprovalDetail.activity.submitted"), when: "Jun 10, 2025 · 09:14" },
                { title: t("adminApprovalDetail.activity.verified"), when: "Jun 10, 2025 · 09:16" },
                { title: t("adminApprovalDetail.activity.assigned"), when: "Jun 10, 2025 · 10:02" },
                { title: t("adminApprovalDetail.activity.awaiting"), when: t("adminApprovalDetail.activity.now") },
              ].map((t) => (
                <li key={t.title} className="flex gap-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: BRAND }} />
                  <div>
                    <p className="text-sm font-medium">{t.title}</p>
                    <p className="text-xs text-muted-foreground">{t.when}</p>
                  </div>
                </li>
              ))}
            </ol>
          </TabsContent>
        </Tabs>

        <Separator />

        {/* Footer actions */}
        <div className="flex flex-wrap items-center gap-3 p-6 sm:p-8">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button className="h-11 rounded-xl bg-emerald-600 px-5 text-white hover:bg-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> {t("adminApprovalDetail.approve")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("adminApprovalDetail.approveTitle", { name: app.name })}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("adminApprovalDetail.approveDesc", { business: app.businessName })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("adminApprovalDetail.cancel")}</AlertDialogCancel>
                <AlertDialogAction className="bg-emerald-600 text-white hover:bg-emerald-700">
                  {t("adminApprovalDetail.approveConfirm")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="h-11 rounded-xl border-rose-200 bg-rose-50 px-5 text-rose-700 hover:bg-rose-100 hover:text-rose-700"
              >
                <Ban className="h-4 w-4" /> {t("adminApprovalDetail.reject")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("adminApprovalDetail.rejectTitle", { name: app.name })}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("adminApprovalDetail.rejectDesc")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("adminApprovalDetail.cancel")}</AlertDialogCancel>
                <AlertDialogAction className="bg-rose-600 text-white hover:bg-rose-700">
                  {t("adminApprovalDetail.rejectConfirm")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button asChild variant="ghost" className="h-11 rounded-xl px-5">
            <Link to="/admin/approvals">{t("adminApprovalDetail.cancel")}</Link>
          </Button>
        </div>
      </Card>

      <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: BRAND }} />
              {previewDoc?.label}
            </DialogTitle>
            <DialogDescription>{previewDoc?.file}</DialogDescription>
          </DialogHeader>

          <div className="grid min-h-[420px] place-items-center rounded-2xl border border-dashed border-border/60 bg-surface p-6">
            <div className="text-center">
              <span
                className="mx-auto grid h-16 w-16 place-items-center rounded-2xl"
                style={{ backgroundColor: "#EEEEFE", color: BRAND }}
              >
                <FileText className="h-8 w-8" />
              </span>
              <p className="mt-4 text-sm font-semibold">{t("adminApprovalDetail.docPreview")}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("adminApprovalDetail.docPreviewHint")}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDoc(null)}>
              {t("adminApprovalDetail.close")}
            </Button>
            <Button
              style={{ backgroundColor: BRAND }}
              className="text-white hover:opacity-90"
              onClick={() => {
                toast.success(t("adminApprovalDetail.downloading", { file: previewDoc?.file }));
              }}
            >
              <Download className="h-4 w-4" /> {t("adminApprovalDetail.download")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

function TabTrigger({
  value,
  icon: Icon,
  label,
}: {
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <TabsTrigger
      value={value}
      className="relative rounded-none border-b-2 border-transparent bg-transparent px-0 py-3 text-sm font-medium text-muted-foreground shadow-none data-[state=active]:border-[color:var(--brand)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--brand)] data-[state=active]:shadow-none"
      style={{ ["--brand" as string]: BRAND }}
    >
      <Icon className="h-4 w-4" />
      <span className="ps-2">{label}</span>
    </TabsTrigger>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{children}</p>;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-end text-sm font-semibold">{value}</dd>
    </div>
  );
}

function RowKV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function StatBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl bg-background p-4 text-center">
      <p className="font-display text-2xl font-bold tracking-tight">{value}</p>
      <p className="mt-1 text-[11px] font-medium text-muted-foreground">{label}</p>
    </div>
  );
}
